import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { catchError, from, map, Observable, of, switchMap } from 'rxjs';
import { LegFiJwtService } from '../../services/auth/legfi-jwt.service';
import { RouteModules } from '../../enums/route-modules.enum';
import { JwtLegFiClaims } from '../../services/auth/jwt-legfi-claims.model';
import { GrowlerService } from '../../components/shared/growler/growler.service';
import { TreasuryConversionStageService } from '../../services/treasury/treasury-conversion-stage.service';
import { TreasuryDrainStateResponse, TreasuryDrainStateService } from '../../services/treasury/treasury-drain-state.service';
import { TreasuryDrainStage } from '../../enums/treasury-drain-stage.enum';

@Injectable()
export class PermissionsGuard implements CanActivate, CanActivateChild
{
    constructor(
            private _router: Router,
            private _growler: GrowlerService,
            private _conversionStage: TreasuryConversionStageService,
            private _drainState: TreasuryDrainStateService,
    ) {
    }

    canActivate(
        next: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ): boolean | UrlTree | Promise<boolean | UrlTree> | Observable<boolean | UrlTree> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();

        // ⚠️ ASKED BEFORE THE FEATURE BLOCK, AND ASKED ASYNCHRONOUSLY (SD-10777, review F-4759
        // and F-76c1). Both halves are load-bearing and each was wrong on its own.
        //
        // BEFORE: the Financial Accounts route carries `feature: 'stripe-treasury.enabled'`, and
        // the feature block below returns early. An organization whose token predates the
        // conversion has no such claim — the claims are built ONCE at token issue — so the menu
        // entry appeared and the route refused, which is precisely the stall this change exists
        // to prevent.
        //
        // ASYNCHRONOUSLY: the stage is fetched over HTTP. Reading it synchronously only ever saw
        // whatever some other component happened to have cached already, so a deep link or a cold
        // load resolved before the answer existed and bounced the one admin who must get through.
        //
        // ⚠️ AND IT CANNOT WIDEN ANYTHING. It admits only an ADMIN, only on the Financial Accounts
        // module, and only while `isConversionInProgressFor()` is true — the two in-flight stages
        // for THIS organization. Everything else falls through to the unchanged checks below.
        if (jwt !== null
            && next.data?.module === RouteModules.FINANCIAL_ACCOUNTS
            && (jwt.admin || jwt.superUser)
        ) {
            return this._conversionStage.fetch(jwt.orgId).pipe(
                map(() => this._conversionStage.isConversionInProgressFor(jwt.orgId)),
                // A failed fetch must not open a route. `fetch()` already degrades to `none`, and
                // this is the belt to that braces.
                catchError(() => of(false)),
                switchMap((inConversion: boolean) => inConversion
                    ? of(true)
                    // ⚠️ AND THE SAME DOOR FOR A ROLLED-BACK ORGANIZATION MID-DRAIN (SD-10965,
                    // review F-d438). Freeze UN-ENROLS the organization, so it loses
                    // `stripe-treasury.enabled` the moment a super confirms — and this page is the
                    // ONLY place it can watch its own money leave over the following weeks. Gated
                    // on the flag alone, every single organization the drain view was written for
                    // was bounced to the dashboard before the panel was ever created.
                    //
                    // ⚠️ IT CANNOT WIDEN ANYTHING, for the same reasons as the branch above: admin
                    // only, Financial Accounts only, and only while the drain is actually running
                    // for THIS organization. At CLOSED the stage leaves this list, the door shuts,
                    // and the organization goes back to the pre-conversion screens — which is the
                    // ticket's own rule.
                    // ⚠️ AND ONLY WHEN THE FLAG IS ACTUALLY ABSENT. An organization that still
                    // holds `stripe-treasury.enabled` passes the feature block below on its own,
                    // so asking about its drain would be a request that can never change the
                    // answer — paid by all 345 organizations already on Treasury, on every
                    // Financial Accounts navigation, for a conversion none of them is in.
                    // Richard's standing rule is that NOTHING outside the conversion may be
                    // impacted, and that explicitly includes the cost of shared paths.
                    : !LegFiJwtService.featureEnabled('stripe-treasury.enabled')
                    ? this._drainState.fetch(jwt.orgId).pipe(
                        map((drain: TreasuryDrainStateResponse): boolean => [
                            TreasuryDrainStage.FINALIZING,
                            TreasuryDrainStage.DRAINING,
                            TreasuryDrainStage.AWAITING_CLOSE,
                        ].includes(drain?.stage)),
                        // A failed fetch must not open a route. `fetch()` already degrades to
                        // `none`; this is the belt to that braces, exactly as above.
                        catchError(() => of(false)),
                        switchMap((draining: boolean) => draining
                            ? of(true)
                            : from(Promise.resolve(this.decide(next, state, jwt)))),
                    )
                    : from(Promise.resolve(this.decide(next, state, jwt)))),
            );
        }

        return this.decide(next, state, jwt);
    }

    /**
     * The ordinary decision, exactly as it was before SD-10777.
     *
     * Split out rather than rewritten: every organization that is not part-way through a
     * conversion reaches this and nothing about it has changed.
     */
    private decide(
        next: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
        jwt: JwtLegFiClaims,
    ): boolean | UrlTree | Promise<boolean | UrlTree> {

        const dashboardView: string[] = [];
        if (LegFiJwtService.featureEnabled('pmc-dashboard.enabled')) {
            dashboardView.push(`pmc/${jwt.pmcId}/dashboard`);
        } else if (jwt?.admin) {
            dashboardView.push('app/organization/dashboard');
        } else {
            dashboardView.push('app/owner/dashboard');
        }
        dashboardView.push(<any>{uri: encodeURIComponent(state.url)});

        if (jwt === null) {
            const loginView = ['/auth/login'];
            loginView.push(<any>{uri: encodeURIComponent(state.url)});
            return this._router.navigate(loginView);
        }

        // no module for permissions specified, all users pass
        if (!next.data.module) {
            return true;
        }

        if (next.data.hasOwnProperty('feature')) {
            // support check for feature enabled or disabled
            const featureCheck = next.data.feature.includes('!')
                ? LegFiJwtService.featureEnabled(next.data.feature)
                : !LegFiJwtService.featureEnabled(next.data.feature);

            if (featureCheck) {
                // allow supers into pmc dashboard
                if (next.data.feature === 'pmc-dashboard.enabled' && jwt.superUser) {
                    return true;
                }

                this._growler.oops('You are not authorized to view the requested feature');
                // redirect if no checks pass
                return this._router.navigate(dashboardView);
            }
        }

        if (next.data.module === RouteModules.SUPER_ADMIN) {
            if (jwt.superUser) {
                return true;
            }
        }

        if (next.data.module === RouteModules.ORG_ADMIN) {
            if (jwt.admin || jwt.superUser) {
                return true;
            }
        }

        const requireWrite = next.data.hasOwnProperty('write') && next.data.write === true;
        if (LegFiJwtService.doesUserHaveModulePermission(next.data.module, requireWrite)) {
            return true;
        }

        this._growler.oops('You are not authorized to view the requested page');
        // redirect if no checks pass
        return this._router.navigate(dashboardView);
    }

    canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        return this.canActivate(childRoute, state);
    }
}
